import React, { FC, useEffect, useState } from "react";

import { useTranslation } from "react-i18next";
import { useMutation, useQuery } from "react-query";
import { useParams } from "react-router-dom";
import { useMount } from "react-use";

import { StandardLayout } from "@/components/layouts";
import { WizardForm } from "@/components/organisms";
import { requestApi } from "@/shared/apis";
import { emptyAddress } from "@/shared/domain";
import { useAuth, useBreadcrumb, useToast } from "@/shared/hooks";
import { useUserFeedback } from "@/userFeedback";

import { CustomerCardSteps } from "./constants";
import CustomerPreview from "./CustomerPreview/CustomerPreview";
import { customerCardDefaultValues, CustomerCardFormType, customerCardMapper } from "./formType";

const CustomerCardPage: FC = () => {
    const { t } = useTranslation();
    const { setPageTitle } = useBreadcrumb();
    const { incUsageCount } = useUserFeedback();
    const { user } = useAuth();
    const { errorToast } = useToast();

    const { id } = useParams();
    const policyholderId = Number(id);

    const [requestId, setRequestId] = useState(null);
    const [documentId, setDocumentId] = useState(null);
    const [representativeInfo, setRepresentativeInfo] = useState(null);

    const { policyHolderLinked: policyholderLinked, accessControlListId: accessRightsId } =
        user.getAccessRightFor(policyholderId);
    useMount(incUsageCount);

    const { data: policyholderDetails = null } = useQuery(
        ["personalDetails", { policyholderId }],
        () => requestApi.getPersonalDetails(+policyholderId, accessRightsId),
        {
            enabled: !!policyholderId,
            refetchOnWindowFocus: false,
        },
    );

    const { currentDetails } = policyholderDetails;

    useEffect(() => {
        if (currentDetails) {
            setPageTitle(currentDetails.fullName);
        }
    }, [currentDetails]);

    const { data: address = emptyAddress } = useQuery(
        ["address", { policyholderId }],
        () => requestApi.getAddress(+policyholderId, accessRightsId),
        {
            enabled: !!policyholderId,
            refetchOnWindowFocus: false,
        },
    );

    const { mutate: submitCustomerCard } = useMutation(requestApi.submitCustomerCardForm, {
        onSuccess: (data) => {
            setRequestId(data.id);
            setDocumentId(data.userRequestDocument[0].id);
        },
        onError: () => {
            errorToast({ message: t("Errors.FormGenericError") });
        },
    });

    const signLocation = address?.currentAddress?.addressDetail?.city || "";

    const defaultFormValues = customerCardDefaultValues(
        policyholderId,
        accessRightsId,
        policyholderLinked,
        policyholderDetails,
        address,
    );

    const handleCustomerCardRequest = (customerCard: CustomerCardFormType) => {
        setRepresentativeInfo(customerCard.officeCommunication?.representativeInfo);
        submitCustomerCard(customerCardMapper(customerCard));
    };

    return (
        <StandardLayout title={t("PageTitles.CustomerCard")} disableSearch>
            {documentId ? (
                <CustomerPreview
                    document={documentId}
                    signedBy={representativeInfo || policyholderDetails.currentDetails.fullName}
                    signedIn={signLocation}
                    requestId={requestId}
                />
            ) : (
                <WizardForm
                    title={t("CustomerCard.General.SubTitle")}
                    wizardSteps={CustomerCardSteps}
                    defaultFormValues={defaultFormValues}
                    handleSubmit={handleCustomerCardRequest}
                />
            )}
        </StandardLayout>
    );
};

export default CustomerCardPage;
